/* goldstein-gift-card-basket-managed */
import {
  basketConflict,
  conflictMessage,
  hasMixedGiftBasket,
  isGiftCardLine,
  missingGiftRecipientLine,
  validateGiftRecipient,
} from './shop-gift-card-basket-rules.js';
import { createGiftCardConsolidator, matchingGiftGroups } from './shop-gift-card-combine.js';

(() => {
  'use strict';

  if (window.__goldsteinGiftBasketInstalled) return;
  window.__goldsteinGiftBasketInstalled = true;

  // Theme label/button display rules can override the browser's hidden rule.
  const visibilityStyle = document.createElement('style');
  visibilityStyle.textContent = '[data-shop-gift-recipient-fields] [hidden]{display:none!important;} form[data-shop-gift-product] .shopify-payment-button, form[data-shop-gift-product] .variant__button-label [data-shopping-option-price]{display:none!important;} body.shop-gift-card-only .additional-checkout-buttons, body.shop-gift-card-only .cart__additional_checkout_buttons{display:none!important;}';
  document.head.append(visibilityStyle);
  const customStyle = document.createElement('style');
  customStyle.textContent = '[data-shop-gift-preset-hidden]{display:none!important;} form[data-shop-gift-product] .variant__button-label{font-size:20px!important;font-weight:600;line-height:1.35;} form[data-shop-gift-custom-active] .shop-option-current{display:none!important;} form[data-shop-gift-custom-active] .variant-field:not(.shop-gift-custom-option) .variant__button-label{background:#f7f2eb!important;border-color:#9b8176!important;} .shop-gift-custom-option input[type=number]::-webkit-inner-spin-button,.shop-gift-custom-option input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none;margin:0;}';
  document.head.append(customStyle);

  const giftVariantIds = new Set();
  const formProducts = new WeakMap();
  const originalFetch = window.fetch.bind(window);
  const consolidateGifts = createGiftCardConsolidator({
    readCart: cartState, fetch: originalFetch,
    prepareUrl: 'https://admin.goldsteinsalmon.co.uk/api/gift-cards/purchase/shopify-combine',
    root: window.Shopify?.routes?.root || '/',
  });
  const originalXhrOpen = XMLHttpRequest.prototype.open;
  const originalXhrSend = XMLHttpRequest.prototype.send;
  const scheduleReadinessUrl = 'https://admin.goldsteinsalmon.co.uk/api/gift-cards/purchase/scheduling-ready';
  let bypassClick = null;
  const bypassSubmit = new WeakSet();
  const pendingForms = new WeakSet();
  let announcement;
  let announcementTimer;
  let lastBasketClass = '';
  let schedulingAvailable = false;
  let latestCart = null;
  const customVariants = new Map();
  function renderGiftTotal(form) {
    const product = productForForm(form).product;
    const variantId = form.querySelector('[name="id"]')?.value;
    const selected = product?.variants?.find(variant => String(variant.id) === String(variantId));
    const custom = form.querySelector('[data-shop-gift-custom-toggle]')?.checked;
    const unitPence = custom
      ? Number(form.querySelector('[data-shop-gift-custom-amount]')?.value) * 100
      : Number(selected?.price);
    const quantity = Number(form.querySelector('[name="quantity"]')?.value || 1);
    const cents = unitPence * quantity;
    const valid = Number.isSafeInteger(cents) && cents > 0 && Number.isSafeInteger(quantity) && quantity > 0;
    const saved = valid ? String(cents) : '0';
    if (form.dataset.shopGiftTotalCents !== saved) form.dataset.shopGiftTotalCents = saved;
    const price = form.querySelector('[data-shop-current-price]');
    const text = valid ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(cents / 100) : 'Price unavailable.';
    if (price && price.textContent !== text) price.textContent = text;
  }

  async function prepareCustomAmount(form) {
    const chosen = form.querySelector('[data-shop-gift-custom-toggle]');
    if (!chosen?.checked) return;
    const amount = Number(form.querySelector('[data-shop-gift-custom-amount]')?.value);
    if (!Number.isSafeInteger(amount) || !Number.isSafeInteger(amount * 100) || amount < 1) {
      throw new Error('Choose a positive whole-pound gift-card amount.');
    }
    let id = customVariants.get(amount);
    if (!id) {
      const response = await originalFetch('https://admin.goldsteinsalmon.co.uk/api/gift-cards/purchase/shopify-combine', {
        method: 'POST', mode: 'cors', credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customAmount: amount }), signal: AbortSignal.timeout(30_000),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'The custom amount is unavailable. Please try again.');
      if (result.amountPence !== amount * 100 || !/^\d+$/.test(String(result.variantId))) {
        throw new Error('We could not confirm your custom gift-card price.');
      }
      id = String(result.variantId);
      customVariants.set(amount, id);
    }
    if (!chosen.checked || Number(form.querySelector('[data-shop-gift-custom-amount]')?.value) !== amount) {
      throw new Error('Your chosen amount changed. Please try adding the card again.');
    }
    const variantInput = form.querySelector('[name="id"]');
    if (!variantInput) throw new Error('The gift-card variant control is unavailable.');
    if (variantInput instanceof HTMLSelectElement && ![...variantInput.options].some(option => option.value === id)) {
      variantInput.add(new Option(`£${amount.toFixed(2)} GBP`, id));
    }
    variantInput.value = id;
    giftVariantIds.add(id);
  }

  function updateGiftPageWarning(cart) {
    const conflict = (cart.items || []).some(item => !isGiftCardLine(item));
    document.querySelectorAll('[data-shop-gift-recipient-fields]').forEach(group => {
      const warning = group.querySelector('[data-shop-gift-basket-warning]');
      if (warning) warning.hidden = !conflict;
    });
  }

  function showMessage(message, error = true) {
    if (!announcement) {
      announcement = document.createElement('div');
      announcement.setAttribute('role', 'alert');
      announcement.setAttribute('aria-live', 'assertive');
      announcement.style.cssText = [
        'position:fixed', 'z-index:2147483000', 'left:16px', 'right:16px', 'top:16px',
        'max-width:760px', 'margin:0 auto', 'padding:15px 18px', 'border-radius:8px',
        'box-shadow:0 8px 28px rgba(36,24,23,.18)', 'font:600 15px/1.45 sans-serif',
        'white-space:normal',
      ].join(';');
      document.body.append(announcement);
    }
    announcement.style.background = error ? '#f7e7df' : '#e5eee2';
    announcement.style.color = '#241817';
    announcement.style.border = '1px solid #9b8176';
    announcement.textContent = message;
    announcement.hidden = false;
    clearTimeout(announcementTimer);
    announcementTimer = setTimeout(() => { if (announcement) announcement.hidden = true; }, 9000);
  }

  function isScheduledGiftProperties(properties = {}) {
    return Boolean(properties['_Goldstein send at'] || properties['Send on']);
  }

  async function refreshSchedulingReadiness() {
    document.querySelectorAll('[data-shop-gift-schedule-status]').forEach(status => {
      status.textContent = 'Checking scheduled-send availability…';
      status.hidden = status.closest('fieldset')?.querySelector('input[value="now"]')?.checked === true;
    });
    document.querySelectorAll('[data-shop-gift-schedule-retry]').forEach(button => { button.hidden = true; });
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 4500);
      try {
        const response = await originalFetch(scheduleReadinessUrl, {
          method: 'GET',
          mode: 'cors',
          credentials: 'omit',
          cache: 'no-store',
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
        if (!response.ok) throw new Error('Schedule readiness unavailable');
        const body = await response.json();
        schedulingAvailable = body?.ready === true;
      } finally {
        clearTimeout(timer);
      }
    } catch {
      schedulingAvailable = false;
    }
    document.querySelectorAll('[data-shop-gift-schedule-fields]').forEach(schedule => {
      const later = schedule.querySelector('input[name="shop-gift-card-send-mode"][value="date"]');
      const immediate = schedule.querySelector('input[name="shop-gift-card-send-mode"][value="now"]');
      const status = schedule.querySelector('[data-shop-gift-schedule-status]');
      const retry = schedule.querySelector('[data-shop-gift-schedule-retry]');
      if (!schedulingAvailable && later?.checked) {
        later.checked = false;
        if (immediate) immediate.checked = false;
        const form = schedule.closest('form');
        if (form) clearScheduleProperties(form);
        schedule.querySelectorAll('[name="properties[Delivery date (UK)]"], [name="properties[Delivery time (UK)]"]').forEach(field => {
          field.value = '';
          field.disabled = true;
          field.required = false;
          field.closest('label')?.setAttribute('hidden', '');
        });
        schedule.querySelectorAll('p').forEach(paragraph => {
          if (paragraph.textContent.includes('within one hour')) paragraph.hidden = true;
        });
      }
      if (later) later.disabled = !schedulingAvailable;
      if (status) status.textContent = schedulingAvailable
        ? 'Scheduled email is available. UK time is used; Shopify sends within one hour of the chosen time.'
        : 'Scheduled sending is unavailable right now. Choose immediate delivery or try again later.';
      if (status) status.hidden = immediate?.checked === true;
      if (retry) retry.hidden = schedulingAvailable || immediate?.checked === true;
    });
    return schedulingAvailable;
  }

  function clearScheduleProperties(form) {
    for (const name of ['Send on', '_Goldstein send at']) {
      const field = form.elements.namedItem(`properties[${name}]`);
      if (field) { field.value = ''; field.disabled = true; }
    }
  }

  function productForForm(form) {
    if (formProducts.has(form)) return formProducts.get(form);
    const scope = form.closest('[data-product-section], .product-single, .product__info-wrapper, section') || document;
    const jsonNode = scope.querySelector('[data-product-json]') || document.querySelector('[data-product-json]');
    let product = null;
    if (jsonNode) {
      try { product = JSON.parse(jsonNode.textContent); } catch { product = null; }
    }
    if (!product && window.ShopifyAnalytics?.meta?.product) product = window.ShopifyAnalytics.meta.product;
    const handle = String(product?.handle || window.location.pathname.split('/').filter(Boolean).pop() || '');
    const title = String(product?.title || document.querySelector('h1')?.textContent || '');
    const isGiftCard = isGiftCardLine({
      handle,
      title,
      product_type: product?.product_type || product?.type || '',
    });
    const record = { product, isGiftCard };
    formProducts.set(form, record);
    if (isGiftCard) {
      (product?.variants || []).forEach(variant => giftVariantIds.add(String(variant.id)));
    }
    return record;
  }

  function createField(labelText, name, type, autocomplete) {
    const wrapper = document.createElement('label');
    wrapper.style.cssText = 'display:block;margin:0 0 14px;color:#403029;font-size:14px;font-weight:600;';
    const label = document.createElement('span');
    label.textContent = labelText;
    label.style.cssText = 'display:block;margin-bottom:6px;';
    const input = type === 'textarea' ? document.createElement('textarea') : document.createElement('input');
    input.name = `properties[${name}]`;
    input.required = true;
    input.autocomplete = autocomplete || 'off';
    input.setAttribute('aria-required', 'true');
    input.style.cssText = 'box-sizing:border-box;width:100%;max-width:100%;min-width:0;min-height:44px;padding:10px 12px;border:1px solid #9b8176;border-radius:5px;background:#fdfaf5;color:#241817;font:inherit;';
    if (type === 'email') input.type = 'email';
    if (type === 'date') input.type = 'date';
    if (type === 'time') input.type = 'time';
    if (type === 'textarea') {
      input.rows = 3;
      input.style.resize = 'vertical';
    }
    wrapper.append(label, input);
    return { wrapper, input };
  }

  function installGiftFields(form) {
    if (form.querySelector('[data-shop-gift-recipient-fields]')) return;
    form.dataset.shopGiftProduct = '';
    // Keep the preset chooser short; on-demand denominations are still valid
    // native variants, but belong to the custom amount control, not this grid.
    form.querySelectorAll('.variant__button-label').forEach(label => {
      const amount = Number(label.childNodes[0]?.textContent?.replace(/[£,\s]|GBP/g, ''));
      if ([10, 25, 50, 75, 100].includes(amount)) return;
      const wrapper = label.parentElement;
      if (wrapper?.querySelectorAll('.variant__button-label').length === 1) wrapper.dataset.shopGiftPresetHidden = '';
    });
    const group = document.createElement('fieldset');
    group.dataset.shopGiftRecipientFields = '';
    group.style.cssText = 'box-sizing:border-box;min-width:0;max-width:100%;margin:20px 0;padding:16px;border:1px solid #d9c8bb;border-radius:8px;';
    const legend = document.createElement('legend');
    legend.textContent = 'Gift card recipient';
    legend.style.cssText = 'padding:0 6px;color:#403029;font-size:15px;font-weight:700;';
    group.append(legend);

    const basketWarning = document.createElement('div');
    basketWarning.dataset.shopGiftBasketWarning = '';
    basketWarning.setAttribute('role', 'status');
    basketWarning.hidden = true;
    basketWarning.style.cssText = 'margin:0 0 18px;padding:14px;border:1px solid #9b8176;border-radius:6px;background:#f7e7df;color:#241817;font-size:14px;line-height:1.6;';
    const warningText = document.createElement('p');
    warningText.textContent = 'Gift cards need a separate purchase. Your basket already contains other products. Complete that order or remove those items before buying a gift card. Your basket has not been changed.';
    warningText.style.margin = '0 0 8px';
    const basketLink = document.createElement('a');
    basketLink.href = `${window.Shopify?.routes?.root || '/'}cart`;
    basketLink.textContent = 'View your basket';
    basketLink.style.cssText = 'color:inherit;text-decoration:underline;font-weight:600;';
    basketWarning.append(warningText, basketLink);
    group.append(basketWarning);
    const combineNote = document.createElement('p');
    combineNote.textContent = 'Your recipient will receive one gift card for the total amount shown above.';
    combineNote.style.cssText = 'margin:0 0 16px;font-size:13px;line-height:1.6;color:#573e37;';
    group.append(combineNote);
    const customWrap = document.createElement('div');
    customWrap.className = 'variant-field shop-gift-custom-option';
    const customLabel = document.createElement('label');
    customLabel.className = 'variant__button-label shop-gift-custom-label';
    customLabel.style.cssText = 'display:flex;flex-direction:column;justify-content:center;gap:6px;min-height:68px;padding:10px;text-align:center;';
    const customToggle = document.createElement('input');
    customToggle.type = 'radio';
    customToggle.name = 'shop-gift-custom-mode';
    customToggle.id = `shop-gift-custom-mode-${document.querySelectorAll('[data-shop-gift-custom-toggle]').length}`;
    customToggle.className = 'visually-hidden variant-input';
    customToggle.dataset.shopGiftCustomToggle = '';
    customToggle.style.display = 'none';
    customLabel.htmlFor = customToggle.id;
    const customTitle = document.createElement('span');
    customTitle.textContent = 'Custom amount';
    customTitle.style.cssText = 'font-size:14px;font-weight:600;';
    customLabel.append(customTitle);
    const customAmountLabel = document.createElement('label');
    customAmountLabel.style.cssText = 'display:flex;justify-content:center;align-items:center;gap:4px;min-width:0;';
    customAmountLabel.append(document.createTextNode('£'));
    const customAmount = document.createElement('input');
    customAmount.type = 'number';
    customAmount.min = '1'; customAmount.step = '1';
    customAmount.inputMode = 'numeric';
    customAmount.setAttribute('aria-label', 'Custom gift-card amount in pounds');
    customAmount.placeholder = 'Amount';
    customAmount.dataset.shopGiftCustomAmount = '';
    customAmount.style.cssText = 'box-sizing:border-box;width:100%;max-width:105px;min-width:0;padding:4px 0;border:0;background:transparent;color:#241817;text-align:center;font-size:20px;font-weight:600;appearance:textfield;';
    customAmountLabel.append(customAmount);
    let originalId = '';
    const reflectCustom = () => {
      customAmount.required = customToggle.checked;
      customAmount.readOnly = !customToggle.checked;
      form.toggleAttribute('data-shop-gift-custom-active', customToggle.checked);
      customLabel.style.backgroundColor = customToggle.checked ? '#dcc6b6' : '#f7f2eb';
      customLabel.style.borderColor = '#9b8176';
      renderGiftTotal(form);
    };
    customToggle.addEventListener('change', () => {
      const idInput = form.querySelector('[name="id"]');
      if (customToggle.checked) originalId = idInput?.value || '';
      else if (idInput && originalId) idInput.value = originalId;
      reflectCustom();
    });
    customAmount.addEventListener('focus', () => {
      if (!customToggle.checked) {
        originalId = form.querySelector('[name="id"]')?.value || '';
        customToggle.checked = true;
        reflectCustom();
      }
    });
    form.querySelectorAll('.variant__button-label').forEach(label => {
      const input = document.getElementById(label.htmlFor);
      input?.addEventListener('change', () => { customToggle.checked = false; reflectCustom(); });
      label.addEventListener('click', () => {
        customToggle.checked = false;
        const variant = productForForm(form).product?.variants?.find(value => value.title === input?.value || value.option1 === input?.value);
        const idInput = form.querySelector('[name="id"]');
        if (idInput && variant) idInput.value = String(variant.id);
        else if (idInput && input?.checked && originalId) idInput.value = originalId;
        reflectCustom();
      });
    });
    form.addEventListener('input', reflectCustom);
    form.addEventListener('change', () => setTimeout(reflectCustom, 0));
    form.addEventListener('click', event => {
      if (event.target.closest('.js-qty__adjust, [data-quantity-plus], [data-quantity-minus]')) {
        setTimeout(reflectCustom, 0);
      }
    });
    // No nested labels: the number field and radio-label share one styled tile.
    const amountRow = document.createElement('span');
    amountRow.style.cssText = 'display:flex;justify-content:center;align-items:center;gap:4px;';
    amountRow.append(document.createTextNode('£'), customAmount);
    customLabel.append(amountRow);
    customWrap.append(customToggle, customLabel);
    const valuesGrid = form.querySelector('.variant-field')?.parentElement || form;
    valuesGrid.append(customWrap);
    reflectCustom();

    const fields = [
      createField('Recipient name *', 'Recipient name', 'text', 'name'),
      createField('Recipient email *', 'Recipient email', 'email', 'email'),
      createField('Gift note *', 'Message', 'textarea', 'off'),
    ];
    fields.forEach(field => group.append(field.wrapper));

    const schedule = document.createElement('fieldset');
    schedule.dataset.shopGiftScheduleFields = '';
    schedule.style.cssText = 'box-sizing:border-box;min-width:0;max-width:100%;margin:14px 0 0;padding:12px;border:1px solid #d9c8bb;border-radius:6px;';
    const scheduleLegend = document.createElement('legend');
    scheduleLegend.textContent = 'When should the gift-card email arrive?';
    scheduleLegend.style.cssText = 'padding:0 5px;color:#403029;font-size:14px;font-weight:600;';
    const modeWrap = document.createElement('div');
    modeWrap.style.cssText = 'display:flex;flex-wrap:wrap;gap:16px;margin-bottom:10px;';
    const immediateLabel = document.createElement('label');
    const immediate = document.createElement('input');
    immediate.type = 'radio';
    immediate.name = 'shop-gift-card-send-mode';
    immediate.value = 'now';
    immediate.checked = true;
    immediateLabel.append(immediate, document.createTextNode(' Send immediately'));
    const scheduledLabel = document.createElement('label');
    const scheduled = document.createElement('input');
    scheduled.type = 'radio';
    scheduled.name = 'shop-gift-card-send-mode';
    scheduled.value = 'date';
    scheduled.disabled = true;
    scheduledLabel.append(scheduled, document.createTextNode(' Choose UK date & time'));
    modeWrap.append(immediateLabel, scheduledLabel);
    const deliveryDate = createField('Delivery date (UK) *', 'Delivery date (UK)', 'date', 'off');
    const deliveryTime = createField('Delivery time (UK) *', 'Delivery time (UK)', 'time', 'off');
    deliveryDate.input.disabled = true;
    deliveryDate.input.required = false;
    deliveryDate.input.min = londonToday();
    deliveryTime.input.disabled = true;
    deliveryTime.input.required = false;
    deliveryDate.wrapper.hidden = true;
    deliveryTime.wrapper.hidden = true;
    const timingHelp = document.createElement('p');
    timingHelp.textContent = 'Times are UK local time (Europe/London). Shopify sends within one hour of your chosen time.';
    timingHelp.style.cssText = 'margin:8px 0 0;color:#6b4a3d;font-size:12px;line-height:1.5;';
    timingHelp.hidden = true;
    const readinessNote = document.createElement('p');
    readinessNote.dataset.shopGiftScheduleStatus = '';
    readinessNote.hidden = true;
    readinessNote.setAttribute('role', 'status');
    readinessNote.textContent = 'Checking scheduled-send availability…';
    readinessNote.style.cssText = 'margin:8px 0 0;color:#6b4a3d;font-size:12px;line-height:1.5;';
    const retryReadiness = document.createElement('button');
    retryReadiness.type = 'button';
    retryReadiness.dataset.shopGiftScheduleRetry = '';
    retryReadiness.textContent = 'Check again';
    retryReadiness.hidden = true;
    retryReadiness.style.cssText = 'margin-top:8px;padding:6px 0;color:#573e37;font-size:12px;font-weight:600;text-decoration:underline;cursor:pointer;';
    retryReadiness.addEventListener('click', async () => {
      retryReadiness.disabled = true;
      try { await refreshSchedulingReadiness(); }
      finally { retryReadiness.disabled = false; }
    });
    const sendOn = document.createElement('input');
    sendOn.type = 'hidden';
    sendOn.name = 'properties[Send on]';
    sendOn.disabled = true;
    const sendAt = document.createElement('input');
    sendAt.type = 'hidden';
    sendAt.name = 'properties[_Goldstein send at]';
    sendAt.disabled = true;
    const reflectTiming = () => {
      const isScheduled = scheduled.checked;
      deliveryDate.wrapper.hidden = !isScheduled;
      deliveryTime.wrapper.hidden = !isScheduled;
      timingHelp.hidden = !isScheduled;
      readinessNote.hidden = immediate.checked;
      retryReadiness.hidden = immediate.checked || schedulingAvailable;
      deliveryDate.input.disabled = !isScheduled;
      deliveryDate.input.required = isScheduled;
      deliveryTime.input.disabled = !isScheduled;
      deliveryTime.input.required = isScheduled;
      sendOn.disabled = !isScheduled;
      sendAt.disabled = !isScheduled;
      if (!isScheduled) {
        deliveryDate.input.value = '';
        deliveryTime.input.value = '';
        sendOn.value = '';
        sendAt.value = '';
      }
    };
    immediate.addEventListener('change', reflectTiming);
    scheduled.addEventListener('change', reflectTiming);
    schedule.append(scheduleLegend, modeWrap, deliveryDate.wrapper, deliveryTime.wrapper, timingHelp, readinessNote, retryReadiness);
    group.append(schedule);
    group.append(sendOn, sendAt);

    const send = document.createElement('input');
    send.type = 'hidden';
    send.name = 'properties[__shopify_send_gift_card_to_recipient]';
    send.value = 'true';
    group.append(send);

    const submit = form.querySelector('[type="submit"], button[name="add"]');
    if (submit?.parentNode) submit.parentNode.insertBefore(group, submit);
    else form.append(group);
    if (latestCart) updateGiftPageWarning(latestCart);
    void refreshSchedulingReadiness();
  }

  function scan() {
    document.querySelectorAll('.product-option, .cart-item__property, [data-cart-item-property], dl > div').forEach(row => {
      const label = row.querySelector('dt, .product-option__name, [data-property-name]')?.textContent?.trim();
      const firstLine = row.textContent?.trim().split(/[:\n]/, 1)[0]?.trim();
      if (/^Send on$/i.test(label || firstLine || '')) row.hidden = true;
    });
    document.querySelectorAll('form').forEach(form => {
      if (!/\/cart\/add(?:\.js)?(?:[?#]|$)/.test(form.getAttribute('action') || '')) return;
      const record = productForForm(form);
      if (record.isGiftCard) {
        form.dataset.shopGiftCardProduct = 'true';
        installGiftFields(form);
        renderGiftTotal(form);
      }
    });
  }

  function propertyValue(form, propertyName) {
    return form.elements.namedItem(`properties[${propertyName}]`)?.value ?? '';
  }

  function syncGiftSchedule(form) {
    const selectedMode = form.querySelector('input[name="shop-gift-card-send-mode"]:checked');
    if (!selectedMode) {
      showMessage('Choose immediate delivery or a scheduled UK date and time.');
      return false;
    }
    const scheduled = form.querySelector('input[name="shop-gift-card-send-mode"][value="date"]')?.checked;
    const sendOn = form.elements.namedItem('properties[Send on]');
    const sendAt = form.elements.namedItem('properties[_Goldstein send at]');
    if (!scheduled) {
      if (sendOn) { sendOn.value = ''; sendOn.disabled = true; }
      if (sendAt) { sendAt.value = ''; sendAt.disabled = true; }
      return true;
    }
    if (!schedulingAvailable) {
      clearScheduleProperties(form);
      showMessage(conflictMessage('schedule-unavailable'));
      return false;
    }
    const dateInput = form.elements.namedItem('properties[Delivery date (UK)]');
    const timeInput = form.elements.namedItem('properties[Delivery time (UK)]');
    try {
      const date = dateInput?.value || '';
      const time = timeInput?.value || '';
      const iso = londonDateTimeToIso(date, time);
      const holdDate = holdDateFor(iso);
      if (!dateInput || !timeInput || !sendOn || !sendAt) throw new Error('The scheduling fields are unavailable.');
      sendOn.value = holdDate;
      sendAt.value = iso;
      sendOn.disabled = false;
      sendAt.disabled = false;
      dateInput.setCustomValidity('');
      timeInput.setCustomValidity('');
      return true;
    } catch (error) {
      if (dateInput) {
        dateInput.setCustomValidity(error.message || 'Choose a valid UK date and time.');
        dateInput.reportValidity();
      }
      showMessage(error.message || 'Choose a valid future UK date and time.');
      return false;
    }
  }

  function validateGiftForm(form) {
    if (!syncGiftSchedule(form)) return false;
    const inputs = {
      'Recipient name': form.elements.namedItem('properties[Recipient name]'),
      'Recipient email': form.elements.namedItem('properties[Recipient email]'),
      Message: form.elements.namedItem('properties[Message]'),
      'Send on': form.elements.namedItem('properties[Send on]'),
      '_Goldstein send at': form.elements.namedItem('properties[_Goldstein send at]'),
      'Delivery date (UK)': form.elements.namedItem('properties[Delivery date (UK)]'),
      'Delivery time (UK)': form.elements.namedItem('properties[Delivery time (UK)]'),
      __shopify_send_gift_card_to_recipient: form.elements.namedItem('properties[__shopify_send_gift_card_to_recipient]'),
    };
    const result = validateGiftRecipient(Object.fromEntries(
      Object.entries(inputs).map(([key, input]) => [key, input?.value ?? '']),
    ));
    for (const key of ['Recipient name', 'Recipient email', 'Message']) {
      const input = inputs[key];
      if (input) input.value = input.value.trim();
    }
    if (!result.valid) {
      const targets = {
        'recipient-name': inputs['Recipient name'],
        'recipient-email': inputs['Recipient email'],
        message: inputs.Message,
        'send-date': inputs['Delivery date (UK)'],
        'send-flag': inputs.__shopify_send_gift_card_to_recipient,
      };
      const target = targets[result.reason];
      target?.setCustomValidity(result.reason === 'recipient-email'
        ? 'Enter a valid recipient email address.'
        : result.reason === 'send-date'
          ? 'Choose a future UK date and time. Shopify sends within one hour of the chosen time.'
          : 'This gift-card field is required.');
      target?.reportValidity();
      target?.addEventListener('input', () => target.setCustomValidity(''), { once: true });
      showMessage(result.reason === 'send-date'
        ? 'Choose a valid future UK date and time for the gift-card email.'
        : 'Enter a recipient name, valid email address, and gift note before adding the card.');
      return false;
    }
    Object.entries(result.properties).forEach(([key, value]) => {
      if (inputs[key]) inputs[key].value = value;
    });
    return true;
  }

  function readBody(body) {
    if (!body) return {};
    if (body instanceof FormData || body instanceof URLSearchParams) {
      const output = {};
      body.forEach((value, key) => { output[key] = String(value); });
      return output;
    }
    if (typeof body === 'string') {
      try { return JSON.parse(body); } catch {
        return Object.fromEntries(new URLSearchParams(body).entries());
      }
    }
    return {};
  }

  async function requestBody(input, init) {
    if (init?.body) return readBody(init.body);
    if (input instanceof Request) {
      try {
        const body = await input.clone().text();
        return readBody(body);
      } catch { return {}; }
    }
    return {};
  }

  function isGiftPayload(payload) {
    const variantId = String(payload.id || payload.variant_id || '');
    return giftVariantIds.has(variantId);
  }

  function londonParts(date) {
    return Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit',
      minute: '2-digit', second: '2-digit', hourCycle: 'h23',
    }).formatToParts(date).map(part => [part.type, part.value]));
  }

  function londonToday() {
    const parts = londonParts(new Date());
    return `${parts.year}-${parts.month}-${parts.day}`;
  }

  function londonDateTimeToIso(date, time) {
    const dateMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    const timeMatch = time.match(/^([01]\d|2[0-3]):([0-5]\d)$/);
    if (!dateMatch || !timeMatch) throw new Error('Choose a valid UK date and time.');
    const [year, month, day] = dateMatch.slice(1).map(Number);
    const [hour, minute] = timeMatch.slice(1).map(Number);
    const wallUtc = Date.UTC(year, month - 1, day, hour, minute);
    const calendarCheck = new Date(wallUtc);
    if (calendarCheck.getUTCFullYear() !== year || calendarCheck.getUTCMonth() !== month - 1 || calendarCheck.getUTCDate() !== day) {
      throw new Error('Choose a valid calendar date.');
    }
    let instant = wallUtc;
    for (let attempt = 0; attempt < 4; attempt += 1) {
      const parts = londonParts(new Date(instant));
      const represented = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), Number(parts.hour), Number(parts.minute), Number(parts.second));
      const difference = wallUtc - represented;
      if (!difference) break;
      instant += difference;
    }
    const result = new Date(instant);
    const resolved = londonParts(result);
    if (Number(resolved.year) !== year || Number(resolved.month) !== month || Number(resolved.day) !== day || Number(resolved.hour) !== hour || Number(resolved.minute) !== minute) {
      throw new Error('That UK local time does not exist because the clocks change. Choose another time.');
    }
    if (instant <= Date.now()) throw new Error('Choose a future UK date and time.');
    return result.toISOString();
  }

  function holdDateFor(sendAt) {
    const parts = londonParts(new Date(sendAt));
    const hold = new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day) + 2));
    return `${hold.getUTCFullYear()}-${String(hold.getUTCMonth() + 1).padStart(2, '0')}-${String(hold.getUTCDate()).padStart(2, '0')}`;
  }

  function propertyMap(payload) {
    const properties = payload.properties || {};
    const read = name => payload[`properties[${name}]`] ?? properties[name] ?? '';
    return {
      'Recipient name': read('Recipient name'),
      'Recipient email': read('Recipient email'),
      Message: read('Message'),
      'Send on': read('Send on'),
      '_Goldstein send at': read('_Goldstein send at'),
      'Delivery date (UK)': read('Delivery date (UK)'),
      'Delivery time (UK)': read('Delivery time (UK)'),
      __shopify_send_gift_card_to_recipient: read('__shopify_send_gift_card_to_recipient'),
    };
  }

  async function cartState() {
    const response = await originalFetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Basket could not be checked.');
    return response.json();
  }

  function updateCartPresentation(cart) {
    latestCart = cart;
    updateGiftPageWarning(cart);
    let combineNotice = document.querySelector('[data-shop-gift-combine-notice]');
    if (!combineNotice && document.querySelector('a[href="/checkout"], button[name="checkout"], input[name="checkout"]')) {
      combineNotice = document.createElement('p');
      combineNotice.dataset.shopGiftCombineNotice = '';
      combineNotice.style.cssText = 'margin:14px 0;padding:12px;border:1px solid #d9c8bb;border-radius:6px;font-size:14px;line-height:1.6;color:#403029;';
      const checkout = document.querySelector('a[href="/checkout"], button[name="checkout"], input[name="checkout"]');
      checkout.parentNode.insertBefore(combineNotice, checkout);
      combineNotice.textContent = 'Matching gifts will become one higher-value gift card at checkout, with one code per recipient, gift note and send time.';
    }
    if (combineNotice) combineNotice.hidden = matchingGiftGroups(cart).length === 0;
    if (!document.body) return;
    const lines = (cart.items || []).filter(item => Number(item.quantity ?? 1) > 0);
    const giftOnly = lines.length > 0 && lines.every(isGiftCardLine);
    const mixed = hasMixedGiftBasket(cart);
    const nextClass = giftOnly ? 'gift-only' : mixed ? 'mixed' : 'standard';
    if (nextClass === lastBasketClass) return;
    lastBasketClass = nextClass;
    document.body.classList.toggle('shop-gift-card-only', giftOnly);
    document.body.classList.toggle('shop-gift-card-mixed', mixed);
    document.body.dataset.shopGiftBasket = nextClass;
    let style = document.getElementById('shop-gift-card-fulfillment-bypass');
    if (!style) {
      style = document.createElement('style');
      style.id = 'shop-gift-card-fulfillment-bypass';
      style.textContent = `
        body.shop-gift-card-only [data-shop-order-notes],
        body.shop-gift-card-only textarea[name="note"],
        body.shop-gift-card-only label:has(textarea[name="note"]),
        body.shop-gift-card-only .cart__note,
        body.shop-gift-card-only .cart-note,
        body.shop-gift-card-only [data-cart-note],
        body.shop-gift-card-only [name="attributes[Gift message]"],
        body.shop-gift-card-only [name*="Gift note"],
        body.shop-gift-card-only [name*="gift_note"],
        body.shop-gift-card-only [name*="gift-note"],
        body.shop-gift-card-only [data-basket-gift-note],
        body.shop-gift-card-only .cart-gift-note,
        body.shop-gift-card-only label:has([name*="Gift note"]),
        body.shop-gift-card-only label:has([name*="gift_note"]),
        body.shop-gift-card-only label:has([name*="gift-note"]),
        body.shop-gift-card-only [name="attributes[Delivery instructions]"],
        body.shop-gift-card-only [name="attributes[Delivery notes]"],
        body.shop-gift-card-only [name*="delivery_instruction"],
        body.shop-gift-card-only [name*="delivery-instructions"],
        body.shop-gift-card-only [name*="delivery_notes"],
        body.shop-gift-card-only .cart label:has([name*="delivery"]),
        body.shop-gift-card-only .cart__footer label:has([name*="delivery"]),
        body.shop-gift-card-only .cart-drawer label:has([name*="delivery"]),
        body.shop-gift-card-only [data-cart-page] label:has([name*="delivery"]),
        body.shop-gift-card-only #storePickupApp,
        body.shop-gift-card-only .checkoutMethod,
        body.shop-gift-card-only [data-shop-delivery-method],
        body.shop-gift-card-only [data-shop-delivery-date],
        body.shop-gift-card-only [data-zapiet-date-picker],
        body.shop-gift-card-only #zapiet-delivery-date,
        body.shop-gift-card-only #delivery_date,
        body.shop-gift-card-only #delivery-date,
        body.shop-gift-card-only .zapiet-delivery-date,
        body.shop-gift-card-only .zapiet-date-picker,
        body.shop-gift-card-only .delivery-date-picker { display:none !important; }
        body.shop-gift-card-mixed [data-shop-order-notes] { display:none !important; }
      `;
      document.head.append(style);
    }
  }

  async function refreshCartPresentation() {
    try { updateCartPresentation(await cartState()); }
    catch { /* The checkout preflight remains fail-closed if a basket cannot be read. */ }
  }

  async function checkPurchase(attemptedGift, properties, checkout = false, schedulePrechecked = false) {
    let cart = await cartState();
    if (checkout) {
      if (hasMixedGiftBasket(cart)) return { allowed: false, message: conflictMessage('mixed-existing') };
      if (missingGiftRecipientLine(cart)) return { allowed: false, message: conflictMessage('missing-recipient') };
      if (cart.items?.some(item => isGiftCardLine(item) && isScheduledGiftProperties(item.properties || {}))
        && !await refreshSchedulingReadiness()) {
        return { allowed: false, message: conflictMessage('schedule-unavailable') };
      }
      if (cart.items?.some(isGiftCardLine)) {
        try {
          if (!window.goldsteinCartUpdates?.exclusive) {
            throw new Error('Gift-card basket details are still loading. Please refresh before checkout.');
          }
          cart = await window.goldsteinCartUpdates.exclusive(() => consolidateGifts());
          updateCartPresentation(cart);
        } catch (error) {
          return { allowed: false, message: error.message || 'We could not safely combine the gift cards. Please try again.' };
        }
      }
      return { allowed: true, cart };
    }
    const conflict = basketConflict(cart, attemptedGift);
    if (conflict) return { allowed: false, message: conflictMessage(conflict) };
    if (attemptedGift && isScheduledGiftProperties(properties || {}) && !schedulePrechecked
      && !await refreshSchedulingReadiness()) {
      return { allowed: false, message: conflictMessage('schedule-unavailable') };
    }
    if (attemptedGift) {
      const validation = validateGiftRecipient(properties || {});
      if (!validation.valid) {
        return { allowed: false, message: 'Enter a recipient name, valid email address, and gift note before adding the card.' };
      }
    }
    return { allowed: true, cart };
  }

  function addPath(url) {
    try {
      const path = new URL(url, window.location.href).pathname;
      return /\/cart\/add(?:\.js)?$/.test(path);
    } catch { return false; }
  }

  function checkoutControl(element) {
    if (!element) return false;
    return element.name === 'checkout'
      || element.id === 'checkout'
      || element.id === 'Checkout'
      || /^\/checkouts?(\/|$)/.test(element.getAttribute('href') || '')
      || /^(checkout|proceed to checkout|secure checkout)$/i.test((element.innerText || element.value || '').trim());
  }

  async function guardForm(form, submitter) {
    if (pendingForms.has(form)) return false;
    pendingForms.add(form);
    try {
    const record = productForForm(form);
    const attemptedGift = record.isGiftCard;
    const isScheduledForm = attemptedGift
      && form.querySelector('input[name="shop-gift-card-send-mode"][value="date"]')?.checked;
    if (isScheduledForm && !await refreshSchedulingReadiness()) {
      clearScheduleProperties(form);
      showMessage(conflictMessage('schedule-unavailable'));
      return false;
    }
    if (attemptedGift && !validateGiftForm(form)) return false;
    let properties = {};
    if (attemptedGift) {
      properties = {
        'Recipient name': propertyValue(form, 'Recipient name'),
        'Recipient email': propertyValue(form, 'Recipient email'),
        Message: propertyValue(form, 'Message'),
        'Send on': propertyValue(form, 'Send on'),
        '_Goldstein send at': propertyValue(form, '_Goldstein send at'),
        'Delivery date (UK)': propertyValue(form, 'Delivery date (UK)'),
        'Delivery time (UK)': propertyValue(form, 'Delivery time (UK)'),
        __shopify_send_gift_card_to_recipient: propertyValue(form, '__shopify_send_gift_card_to_recipient'),
      };
    }
    const result = await checkPurchase(attemptedGift, properties, false, Boolean(isScheduledForm));
    if (!result.allowed) {
      if (result.message === conflictMessage('schedule-unavailable')) clearScheduleProperties(form);
      showMessage(result.message);
      return false;
    }
    if (attemptedGift) await prepareCustomAmount(form);
    if (isScheduledForm && submitter
      && (checkoutControl(submitter) || submitter.matches('.shopify-payment-button__button'))
      && !await refreshSchedulingReadiness()) {
      clearScheduleProperties(form);
      showMessage(conflictMessage('schedule-unavailable'));
      return false;
    }
    if (submitter && checkoutControl(submitter)) {
      bypassClick = submitter;
      submitter.click();
      bypassClick = null;
      return false;
    }
    if (submitter?.matches('.shopify-payment-button__button')) {
      bypassClick = submitter;
      submitter.click();
      bypassClick = null;
      return false;
    }
    // Broadcast handles a click, not submit. requestSubmit bypassed its Ajax
    // handler and took every ordinary product to /cart. Keep provider fields
    // intact and replay only after the basket safeguard has approved the add.
    const addButton = submitter?.matches('[data-add-to-cart]') ? submitter
      : form.querySelector('[data-add-to-cart]');
    if (addButton && window.theme?.AjaxAdd) {
      bypassClick = addButton;
      try { addButton.click(); } finally { bypassClick = null; }
      return false;
    }
    bypassSubmit.add(form);
    if (typeof form.requestSubmit === 'function') form.requestSubmit(submitter || undefined);
    else form.submit();
    return false;
    } finally {
      pendingForms.delete(form);
    }
  }

  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (bypassSubmit.has(form)) {
      bypassSubmit.delete(form);
      return;
    }
    if (/\/cart\/add(?:\.js)?(?:[?#]|$)/.test(form.getAttribute('action') || '')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      void guardForm(form, event.submitter).catch(() => showMessage('We could not check your basket. Refresh the page and try again.'));
      return;
    }
    if (event.submitter && checkoutControl(event.submitter)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      void checkPurchase(false, null, true).then(result => {
        if (!result.allowed) return showMessage(result.message);
        if (result.cart.items?.some(isGiftCardLine)) {
          // Do not resubmit old quantity inputs against a normalized cart.
          window.location.assign(`${window.Shopify?.routes?.root || '/'}checkout`);
          return;
        }
        bypassSubmit.add(form);
        form.requestSubmit(event.submitter);
      }).catch(() => showMessage('We could not check your basket. Refresh before checkout.'));
    }
  }, true);

  document.addEventListener('click', event => {
    const control = event.target.closest('button,input,a');
    if (!control || bypassClick === control) return;
    const form = control.form || control.closest('form');
    if (form && /\/cart\/add(?:\.js)?(?:[?#]|$)/.test(form.getAttribute('action') || '')
      && (control.type === 'submit' || control.matches('.shopify-payment-button__button'))) {
      event.preventDefault();
      event.stopImmediatePropagation();
      void guardForm(form, control).catch(() => showMessage('We could not check your basket. Refresh the page and try again.'));
      return;
    }
    if (!checkoutControl(control)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    void checkPurchase(false, null, true).then(result => {
      if (!result.allowed) return showMessage(result.message);
      if (result.cart.items?.some(isGiftCardLine)) {
        window.location.assign(`${window.Shopify?.routes?.root || '/'}checkout`);
        return;
      }
      bypassClick = control;
      control.click();
      bypassClick = null;
    }).catch(() => showMessage('We could not check your basket. Refresh before checkout.'));
  }, true);

  // Cover themes and cart drawers that submit Ajax requests directly rather
  // than dispatching a product-form submit event.
  window.fetch = async function (input, init) {
    const url = input instanceof Request ? input.url : String(input);
    if (!addPath(url)) return originalFetch(input, init);
    const payload = await requestBody(input, init);
    const attemptedGift = isGiftPayload(payload);
    const result = await checkPurchase(attemptedGift, attemptedGift ? propertyMap(payload) : null);
    if (!result.allowed) {
      showMessage(result.message);
      throw new Error(result.message);
    }
    const response = await originalFetch(input, init);
    if (response.ok) {
      try {
        const updated = await originalFetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { credentials: 'same-origin', cache: 'no-store' }).then(value => value.json());
        updateCartPresentation(updated);
        if (hasMixedGiftBasket(updated)) showMessage(conflictMessage('mixed-existing'));
      } catch { /* Checkout preflight will check again. */ }
    }
    return response;
  };

  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this.__goldsteinUrl = url;
    return originalXhrOpen.call(this, method, url, ...rest);
  };
  XMLHttpRequest.prototype.send = function (body) {
    if (!addPath(this.__goldsteinUrl)) return originalXhrSend.call(this, body);
    const xhr = this;
    const payload = readBody(body);
    const attemptedGift = isGiftPayload(payload);
    void checkPurchase(attemptedGift, attemptedGift ? propertyMap(payload) : null).then(result => {
      if (!result.allowed) {
        showMessage(result.message);
        xhr.abort();
        return;
      }
      originalXhrSend.call(xhr, body);
    }).catch(() => {
      showMessage('We could not check your basket. Refresh the page and try again.');
      xhr.abort();
    });
  };

  const observer = new MutationObserver(scan);
  observer.observe(document.documentElement, { childList: true, subtree: true });
  for (const eventName of ['cart:updated', 'cart:change']) {
    document.addEventListener(eventName, () => { void refreshCartPresentation(); });
  }
  if (window.jQuery) {
    window.jQuery(document).ajaxComplete((_event, _xhr, settings) => {
      if (/\/cart\/(change|update|add)(\.js)?$/.test(new URL(settings.url, location.origin).pathname)) {
        void refreshCartPresentation();
      }
    });
  }
  scan();
  void refreshCartPresentation();
})();
